import {
  AllegroCreateResponsibleProducerRequest,
  AllegroResponsibleProducer,
  AllegroResponsibleProducerAddress,
  AllegroResponsibleProducerContact,
  AllegroResponsibleProducerData,
  AllegroUpdateResponsibleProducerRequest,
} from '../../../api/allegro/responsibleproducer/responsibleProducerTypes';
import type { ExcelRow } from '../../excel/types';
import {
  createAllegroResponsibleProducer,
  updateAllegroResponsibleProducer,
} from '../../../api/allegro/responsibleproducer/responsibleProducer';
import parseExcel from '../../excel/parseExcel';
import COLUMNS from './responsibleProducerExcelSchema';
import { showErrorMessage, showSuccessMessage } from '../../../utils/message';
import {
  extractErrorPayload, isAllegroUnauthorized, readString, runTasksSequentially,
} from '../utils';

type UploadTask = () => Promise<AllegroResponsibleProducer>;

function mapAddress(row: ExcelRow): AllegroResponsibleProducerAddress {
  return {
    countryCode: readString(row, COLUMNS.countryCode),
    street: readString(row, COLUMNS.street),
    postalCode: readString(row, COLUMNS.postalCode),
    city: readString(row, COLUMNS.city),
  } as AllegroResponsibleProducerAddress;
}

function mapContact(row: ExcelRow): AllegroResponsibleProducerContact {
  return {
    email: readString(row, COLUMNS.email),
    phoneNumber: readString(row, COLUMNS.phoneNumber),
    formUrl: readString(row, COLUMNS.formUrl),
  };
}

function mapProducerData(row: ExcelRow): AllegroResponsibleProducerData {
  return {
    tradeName: readString(row, COLUMNS.tradeName),
    address: mapAddress(row),
    contact: mapContact(row),
  } as AllegroResponsibleProducerData;
}

function mapCreateRequest(row: ExcelRow): AllegroCreateResponsibleProducerRequest {
  return {
    name: readString(row, COLUMNS.label),
    producerData: mapProducerData(row),
  } as AllegroCreateResponsibleProducerRequest;
}

function mapUpdateRequest(id: string, row: ExcelRow): AllegroUpdateResponsibleProducerRequest {
  return {
    id,
    ...mapCreateRequest(row),
  };
}

function prepareUploadTasks(marketplaceCode: string, rows: ExcelRow[]): UploadTask[] {
  return rows.map((row) => {
    const id = readString(row, COLUMNS.id);

    if (id) {
      return () => updateAllegroResponsibleProducer(marketplaceCode, id, mapUpdateRequest(id, row));
    }

    return () => createAllegroResponsibleProducer(marketplaceCode, mapCreateRequest(row));
  });
}

export default async function uploadResponsibleProducersExcel(
  marketplaceCode: string,
  file: File,
): Promise<void> {
  const rows = await parseExcel(file);
  const tasks = prepareUploadTasks(marketplaceCode, rows);
  const errors: any[] = [];

  await runTasksSequentially(
    tasks,
    (reason) => {
      if (isAllegroUnauthorized(reason)) {
        return;
      }

      errors.push(extractErrorPayload(reason));
    },
  );

  if (errors.length === 0) {
    showSuccessMessage('Responsible producers successfully uploaded.');
  } else {
    showErrorMessage(JSON.stringify(errors));
  }
}
